<template>
  <div class="px-4 sm:px-6 lg:px-8 bg-white">
    <div v-if="showSuccessMessage" class="flex justify-end z-40 w-auto mb-4">
      <Alert
        :html="true"
        :showClose="true"
        type="success"
        @close="() => (showSuccessMessage = false)"
        htmlMessage="De telling is opgeslagen en verzonden"
      ></Alert>
    </div>
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-2xl font-bold text-gray-600 pt-6">Voorraadbeheer</h1>
      </div>
    </div>
    <div class="sm:flex-auto pb-2">
      <button
        v-if="isToDoActive"
        id="editBtn"
        @click.prevent="enableInventoryEdit"
        class="flex justify-self-end py-2 px-2 md:px-4 text-xs md:text-base font-medium text-white bg-primary-default border border-primary-default rounded-full"
      >
        <span class="">Start telling</span>
      </button>
    </div>
    <div class="mt-8 flow-root" v-if="products.length > 0">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <table class="min-w-full divide-y divide-gray-300 border-t border-t-stone-200">
            <thead>
              <tr>
                <th scope="col" class="relative py-4 pl-3 pr-4">
                  <span class="sr-only"></span>
                </th>
                <th scope="col" class="w-3/5 font-bold text-base py-4 pl-4 pr-3 text-left tracking-wide text-gray-600">
                  Omschrijving
                </th>
                <th
                  scope="col"
                  class="w-1/6 font-bold text-base py-4 pl-4 pr-3 text-center tracking-wide text-gray-600"
                >
                  Artikelcode
                </th>
                <th
                  scope="col"
                  class="w-1/6 font-bold text-base py-4 pl-4 pr-3 text-center tracking-wide text-gray-600"
                >
                  Aantal
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
              <tr v-for="product in products" :key="product.productId">
                <td class="relative whitespace-nowrap py-4 text-right text-sm font-medium ps-8">
                  <img :src="product.imageUrl" @error="handleImageError" class="w-1/2 h-8" />
                </td>
                <td class="font-normal text-base whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {{ product.description }}
                </td>
                <td class="font-normal text-base whitespace-nowrap px-3 py-4 text-center text-sm text-gray-500">
                  {{ product.productCode }}
                </td>
                <td
                  v-if="!editInventory"
                  class="font-normal text-base whitespace-nowrap px-3 py-4 text-center text-sm text-gray-500"
                >
                  {{ product.amountInStock }}
                </td>
                <td v-else-if="editInventory" class="pt-3 pb-2 capitalize whitespace-normal align-middle">
                  <div class="border border-gray-400 rounded-md h-8 inline-block ml-16">
                    <MinusIcon
                      class="text-pdsPrimaryColor float-left w-8 border-r border-gray-400 cursor-pointer mr-1"
                      @click.prevent="reduceQuantity(product)"
                    />
                    <input
                      class="max-w-10 float-left h-7 font-bold pl-3 pt-1 align-middle"
                      v-model="product.amountInStock"
                      type="number"
                      min="1"
                      max="999"
                      maxlength="3"
                      autocomplete="off"
                    />
                    <PlusIcon
                      class="text-pdsPrimaryColor float-right w-8 border-l border-gray-400 cursor-pointer ml-1"
                      @click.prevent="increaseQuantity(product)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div class="sm:flex-auto pb-4 pt-4">
      <button
        v-if="editInventory"
        id="saveBtn"
        @click.prevent="saveInventory"
        class="flex justify-self-end py-2 px-2 md:px-4 text-xs md:text-base font-medium text-white bg-primary-default border border-primary-default rounded-full"
      >
        <span class="">Opslaan en verzenden</span>
      </button>
    </div>
  </div>
</template>

<script>
import { PlusIcon, MinusIcon } from '@heroicons/vue/20/solid';
import axios from 'axios';
import { ref, computed, onMounted, onBeforeUnmount, watch, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';

export default {
  name: 'StockOverview',
  components: {
    PlusIcon,
    MinusIcon
  },
  setup() {
    const route = useRoute();
    const products = ref([]);
    const isToDoActive = ref(false);
    const editInventory = ref(false);
    const todoId = ref(null);
    const showSuccessMessage = ref(false);

    onMounted(async () => {
      todoId.value = route.params.id;
      const stockResponse = await axios.get('products/GetConsignmentStockOverview');
      products.value = stockResponse.data;

      if (todoId.value && products.value && products.value.length !== 0) isToDoActive.value = true;
    });

    const enableInventoryEdit = () => {
      editInventory.value = true;
    };

    const reduceQuantity = (product) => {
      if (product.amountInStock === 1) {
        return;
      }
      saveQuantity(product.amountInStock - 1, product);
    };

    const increaseQuantity = (product) => {
      if (product.amountInStock === 999) {
        return;
      }
      saveQuantity(product.amountInStock + 1, product);
    };

    const saveQuantity = (inputValue, product) => {
      let regex = /^[0-9]{1,3}$/;
      let quantity = parseInt(inputValue, 10);
      if (quantity <= 0) {
        quantity = 1;
      }
      if (quantity > 999) {
        quantity = 999;
      }
      if (!regex.test(quantity) || !quantity || quantity <= 0) {
        product.amountInStock = 0;
        return;
      }
      product.amountInStock = quantity;
    };

    const saveInventory = async () => {
      const requestData = {
        todoId: todoId.value,
        products: products.value
      };

      await axios.post('products/UpdateStockInventory', requestData).then((response) => {
        if (response && response.data && response.data.Success) {
          editInventory.value = false;
          isToDoActive.value = false;
          if (response.data.Success) showSuccessMessage.value = true;
        }
      });
    };

    return {
      products,
      isToDoActive,
      enableInventoryEdit,
      editInventory,
      reduceQuantity,
      increaseQuantity,
      saveQuantity,
      saveInventory,
      showSuccessMessage
    };
  }
};
</script>
